import { Suspense, lazy } from 'react';
import type * as React from 'react';
import { Refine } from '@refinedev/core';
import routerBindings from '@refinedev/react-router';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { Toaster } from 'sonner';
import { dataProvider } from '@/api/data-provider';
import { authProvider } from '@/auth/auth-provider';
import { canAccess, landingPathFor } from '@/auth/rbac';
import { useSession } from '@/auth/use-session';
import { AdminLayout } from '@/layout/admin-layout';
import { useTheme } from '@/layout/theme';
import { RESOURCES } from '@/api/resources';
import { CardSkeleton } from '@/components/states';
import { LoginPage } from '@/pages/login';
import { DashboardPage } from '@/pages/dashboard';
import { InterestsPage } from '@/pages/master-data/interests';
import { EventTypesPage } from '@/pages/master-data/event-types';
import { OptionSetsPage } from '@/pages/master-data/option-sets';
import { CategoriesPage } from '@/pages/master-data/categories';
import { MarketplacesPage } from '@/pages/master-data/marketplaces';
import { SupportSettingsPage } from '@/pages/settings/support';
import { LegalEditorPage } from '@/pages/legal/legal-editor';
import { WizardListPage } from '@/pages/wizard/wizard-list';
import { AdminsPage } from '@/pages/admins/admins';
import { UsersPage } from '@/pages/users/users';
import { ModerationPage } from '@/pages/moderation/reports';
import { SearchAnomaliesPage } from '@/pages/moderation/search-anomalies';
import { NotFoundPage } from '@/pages/not-found';

/**
 * Split out of the initial bundle. The charting library and the QR encoder are
 * the two heaviest dependencies in the app and each is reached by one role on
 * one screen — an admin who never opens analytics should not pay for Recharts
 * on every sign-in. `lazy` wants a default export, which this codebase does not
 * use, so each import is adapted rather than the modules changed.
 */
const ClicksPage = lazy(async () => ({
  default: (await import('@/pages/analytics/clicks')).ClicksPage,
}));
const TopProductsPage = lazy(async () => ({
  default: (await import('@/pages/analytics/top-products')).TopProductsPage,
}));
const VendorReportPage = lazy(async () => ({
  default: (await import('@/pages/analytics/vendor-report')).VendorReportPage,
}));
const WizardEditorPage = lazy(async () => ({
  default: (await import('@/pages/wizard/wizard-editor')).WizardEditorPage,
}));
const AcceptInvitePage = lazy(async () => ({
  default: (await import('@/pages/accept-invite')).AcceptInvitePage,
}));
const ForgotPasswordPage = lazy(async () => ({
  default: (await import('@/pages/forgot-password')).ForgotPasswordPage,
}));
const NewPasswordPage = lazy(async () => ({
  default: (await import('@/pages/new-password')).NewPasswordPage,
}));

/**
 * Split for a different reason from the charts: each of these is reached by one
 * role. A `CONTENT_ADMIN` never opens Ops, and a `SUPPORT` admin never opens the
 * catalogue, so neither should download the other's screen to sign in.
 */
const UserDetailPage = lazy(async () => ({
  default: (await import('@/pages/users/user-detail')).UserDetailPage,
}));
const CataloguePage = lazy(async () => ({
  default: (await import('@/pages/catalogue/catalogue')).CataloguePage,
}));
const CalendarEventsPage = lazy(async () => ({
  default: (await import('@/pages/calendar/calendar-events')).CalendarEventsPage,
}));
const OpsPage = lazy(async () => ({
  default: (await import('@/pages/ops/ops')).OpsPage,
}));

/**
 * Refine owns resources, routing bindings and the query client; the screens
 * above own their own layout. `notificationProvider` is deliberately absent —
 * every mutation reports through `lib/notify.ts` so the panel has one voice
 * rather than two competing toast systems.
 */
export function App(): React.JSX.Element {
  const { theme } = useTheme();

  return (
    <Refine
      dataProvider={dataProvider}
      authProvider={authProvider}
      routerProvider={routerBindings}
      resources={[
        { name: RESOURCES.interests, list: '/interests' },
        { name: RESOURCES.childEventTypes, list: '/event-types' },
        { name: RESOURCES.optionSets, list: '/option-sets' },
        { name: RESOURCES.options },
        { name: RESOURCES.categories, list: '/categories' },
        { name: RESOURCES.marketplaces, list: '/marketplaces' },
        { name: RESOURCES.wizards, list: '/wizards', edit: '/wizards/:id' },
        { name: RESOURCES.admins, list: '/admins' },
        { name: RESOURCES.users, list: '/users', show: '/users/:id' },
        { name: RESOURCES.curatedProducts, list: '/catalogue' },
        { name: RESOURCES.calendarEvents, list: '/calendar-events' },
      ]}
      options={{
        disableTelemetry: true,
        warnWhenUnsavedChanges: false,
        reactQuery: { clientConfig: { defaultOptions: { queries: { retry: 0 } } } },
      }}
    >
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          {/* Public, like the two above: someone who has forgotten their password
              has no session by definition. Reached from the sign-in screen and
              from the emailed link respectively. */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/new-password" element={<NewPasswordPage />} />

          <Route element={<RequireSession />}>
            <Route element={<AdminLayout />}>
              <Route index element={<RoleLanding />} />
              <Route
                path="interests"
                element={<Guarded path="/interests" element={<InterestsPage />} />}
              />
              <Route
                path="event-types"
                element={<Guarded path="/event-types" element={<EventTypesPage />} />}
              />
              <Route
                path="option-sets"
                element={<Guarded path="/option-sets" element={<OptionSetsPage />} />}
              />
              <Route
                path="categories"
                element={<Guarded path="/categories" element={<CategoriesPage />} />}
              />
              <Route
                path="marketplaces"
                element={<Guarded path="/marketplaces" element={<MarketplacesPage />} />}
              />
              <Route
                path="settings/support"
                element={<Guarded path="/settings/support" element={<SupportSettingsPage />} />}
              />
              <Route
                path="legal/:slug"
                element={<Guarded path="/legal/privacy-policy" element={<LegalEditorPage />} />}
              />
              <Route
                path="wizards"
                element={<Guarded path="/wizards" element={<WizardListPage />} />}
              />
              <Route
                path="wizards/:id"
                element={<Guarded path="/wizards" element={<WizardEditorPage />} />}
              />
              <Route
                path="catalogue"
                element={<Guarded path="/catalogue" element={<CataloguePage />} />}
              />
              <Route
                path="calendar-events"
                element={<Guarded path="/calendar-events" element={<CalendarEventsPage />} />}
              />
              <Route path="users" element={<Guarded path="/users" element={<UsersPage />} />} />
              <Route
                path="users/:id"
                element={<Guarded path="/users" element={<UserDetailPage />} />}
              />
              <Route
                path="moderation"
                element={<Guarded path="/moderation" element={<ModerationPage />} />}
              />
              <Route
                path="search-anomalies"
                element={<Guarded path="/search-anomalies" element={<SearchAnomaliesPage />} />}
              />
              <Route
                path="analytics/clicks"
                element={<Guarded path="/analytics/clicks" element={<ClicksPage />} />}
              />
              <Route
                path="analytics/top-products"
                element={<Guarded path="/analytics/top-products" element={<TopProductsPage />} />}
              />
              <Route
                path="analytics/vendor-report"
                element={<Guarded path="/analytics/vendor-report" element={<VendorReportPage />} />}
              />
              <Route path="admins" element={<Guarded path="/admins" element={<AdminsPage />} />} />
              <Route path="ops" element={<Guarded path="/ops" element={<OpsPage />} />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>

      <Toaster
        theme={theme}
        position="bottom-right"
        richColors
        closeButton
        toastOptions={{ style: { fontFamily: 'var(--font-sans)' } }}
      />
    </Refine>
  );
}

/**
 * Shown while a split route's chunk arrives. A skeleton in the content column
 * rather than a blank page, so the sidebar stays put and nothing shifts when
 * the screen resolves.
 */
function RouteFallback(): React.JSX.Element {
  return (
    <div className="mx-auto max-w-[1200px] space-y-4 px-6 py-8">
      <CardSkeleton />
      <CardSkeleton />
    </div>
  );
}

/** No session, no panel. Everything below this is behind an admin token. */
function RequireSession(): React.JSX.Element {
  const session = useSession();
  const location = useLocation();

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

function RoleLanding(): React.JSX.Element {
  const session = useSession();
  if (!session) return <Navigate to="/login" replace />;
  if (session.role === 'ANALYST') return <Navigate to={landingPathFor(session.role)} replace />;
  return <DashboardPage />;
}

/**
 * ⚠️ **Presentation, not protection.** This keeps an admin from opening a
 * screen their role has no use for; the server refuses the underlying requests
 * regardless and answers `403 INSUFFICIENT_ROLE`. Removing this component would
 * make the panel messier, not less secure.
 */
function Guarded({
  path,
  element,
}: {
  path: string;
  element: React.JSX.Element;
}): React.JSX.Element {
  const session = useSession();
  if (!canAccess(session?.role, path)) {
    return <Navigate to={session ? landingPathFor(session.role) : '/login'} replace />;
  }
  return element;
}
